import { ConsultCTA } from "../components/ConsultCTA";
import { PageHero } from "../components/PageHero";
import { Eyebrow, Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, PRINCIPLES } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const About = () => {
    usePageMeta(
        "About Us | Speech Horizon — Speech Therapist in Anishabad, Patna",
        "Meet Aman, a professional Speech Therapist at Speech Horizon, Anishabad, Patna — providing individualized, goal-oriented speech and language therapy for children and adults."
    );

    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow="About Us"
                lines={["Meet Your"]}
                highlight="Speech Therapist"
                description="A specialized speech & language therapy practice built on warmth, structure, and genuine care for every individual's communication journey."
            />

            <section className="bg-cream py-20 md:py-28" data-testid="about-therapist-section">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-12">
                    <Reveal className="relative lg:col-span-5">
                        <div className="spotlight pointer-events-none absolute -inset-10 rounded-full" />
                        <div className="relative overflow-hidden rounded-b-[2.5rem] rounded-t-[10rem] border-[6px] border-white shadow-2xl shadow-navy/15">
                            <img
                                src="/Images/Aman.jpeg"
                                alt="Aman, professional Speech Therapist at Speech Horizon, Anishabad, Patna"
                                className="relative h-[420px] w-full rounded-[2.5rem] object-cover object-[center_95%] shadow-xl shadow-navy/10 md:h-[500px]"
                                data-testid="about-therapist-photo"
                            />
                        </div>
                        <div className="absolute -bottom-6 left-1/2 w-max -translate-x-1/2 rounded-2xl bg-navy px-8 py-4 text-center text-cream shadow-xl">
                            <p className="font-jakarta text-lg font-extrabold">Aman</p>
                            <p className="text-xs text-aqua">Speech Therapist · Speech Horizon</p>
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow>Namaste!</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-jakarta text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl">
                                Therapy Planned Around <span className="text-teal">Your</span> Needs
                            </h2>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div className="mt-6 space-y-5 text-base leading-relaxed text-ink/70 md:text-lg">
                                <p>
                                    Namaste! I&rsquo;m Aman, a professional Speech Therapist dedicated to helping
                                    children and adults overcome speech, language, communication, and related
                                    difficulties.
                                </p>
                                <p>
                                    Every individual has unique needs, strengths, and abilities. That is why
                                    therapy is carefully planned around individual needs and specific goals.
                                </p>
                                <p>
                                    Our aim is to help individuals communicate more effectively, express
                                    themselves with greater clarity, and interact confidently in everyday
                                    situations.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.26}>
                            <div className="mt-8 flex flex-wrap gap-3">
                                {["Individualized Care", "Goal-Oriented Therapy", "Family Involved", "Progress-Focused"].map((chip) => (
                                    <span
                                        key={chip}
                                        className="rounded-full border border-teal/25 bg-teal-soft/60 px-5 py-2 text-sm font-semibold text-teal-dark"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="about-approach-section">
                <div className="mx-auto max-w-6xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="Our Philosophy"
                        title="Every Person Is Different. Every Therapy Plan Should Be Different."
                        description="Before beginning therapy, it is important to understand an individual's communication needs, strengths, and areas of difficulty. We aim to make therapy:"
                    />
                    <div className="mx-auto mt-14 grid max-w-4xl gap-x-16 md:grid-cols-2">
                        {PRINCIPLES.map((principle, i) => (
                            <Reveal key={principle} delay={i * 0.07}>
                                <div className="group flex items-baseline gap-6 border-t border-navy/10 py-6 transition-all duration-500 hover:translate-x-2">
                                    <span className="font-jakarta text-3xl font-extrabold text-teal/30 transition-colors duration-500 group-hover:text-peach-deep">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-jakarta text-xl font-bold text-navy md:text-2xl">
                                        {principle}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mx-auto mt-12 max-w-3xl text-center text-base leading-relaxed text-ink/70 md:text-lg">
                            Parents are also involved in the therapy process so that practice is not limited to
                            therapy sessions. This helps individuals apply and develop their communication skills
                            in everyday life.
                        </p>
                    </Reveal>
                </div>
            </section>

            <ConsultCTA />
        </div>
    );
};

export default About;
